import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Location } from '@angular/common';
import { FormGroup } from '@angular/forms';

import { BaseFormComponent } from '@app/forms/base-form/base-form';
import { ApiService } from '@app/services/api/api.service';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { FormError } from '@app/pages/client-traveler-page/client-traveler-page.component';

@Component({
  selector: 'client-traveler-form',
  templateUrl: './client-traveler-form.component.html',
  styleUrl: './client-traveler-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientTravelerFormComponent extends BaseFormComponent<FormError[] | null> {
  apiService = inject(ApiService);
  location = inject(Location);

  override getFields(): FormlyFieldConfig[] {
    return [
      {
        className: 'mb-6',
        fieldGroupClassName: 'd-flex flex-column g-5',
        wrappers: ['fold-card-wrapper'],
        props: {
          label: 'Travel document',
        },
        fieldGroup: [
          {
            key: 'passport.number',
            type: 'input',
            className: 'w-100',
            props: {
              label: 'Passport number',
              placeholder: 'Passport number',
            },
          },
          {
            key: 'passport.expiration',
            type: 'date',
            className: 'w-100',
            props: {
              label: 'Expiry date',
              startYear: 2000,
              endYear: 2050,
            },
          },
        ],
      },
      {
        className: 'mb-6',
        fieldGroupClassName: 'd-flex flex-column flex-md-row g-5',
        wrappers: ['fold-card-wrapper'],
        props: {
          label: 'Contact information',
        },
        fieldGroup: [
          {
            key: 'phones[0]',
            type: 'phone',
            className: 'w-100',
            props: {
              label: 'Phone number',
            },
            validators: {
              validation: ['phone'],
            },
          },
          {
            key: 'emails[0]',
            type: 'input',
            className: 'w-100',
            props: {
              label: 'Email',
              placeholder: 'Email',
            },
            validators: {
              validation: ['email'],
            },
          },
        ],
      },
      {
        className: 'mb-6',
        fieldGroupClassName: 'd-flex flex-column g-5',
        wrappers: ['fold-card-wrapper'],
        props: {
          label: 'Loyalty & Travel IDs',
        },
        fieldGroup: [
          {
            key: 'frequentFlyerNumbers',
            type: 'repeat',
            props: {
              addText: 'Add another program',
            },
            fieldArray: {
              fieldGroupClassName: 'd-flex flex-column flex-md-row g-5',
              fieldGroup: [
                {
                  key: 'airlineId',
                  type: 'dropdown',
                  className: 'w-100',
                  props: {
                    label: 'Frequent flyer program',
                    placeholder: 'Frequent flyer program',
                    options: this.apiService.airlines(),
                  },
                },
                {
                  key: 'number',
                  type: 'input',
                  className: 'w-100',
                  props: {
                    label: 'Frequent flyer number',
                    placeholder: 'Frequent flyer number',
                  },
                },
              ],
            },
          },
          {
            fieldGroupClassName: 'd-flex flex-column flex-md-row g-5',
            fieldGroup: [
              {
                key: 'knownTravelerNumber',
                type: 'input',
                className: 'w-100',
                props: {
                  label: 'Known travel number',
                  placeholder: 'Known travel number',
                },
              },
              {
                key: 'globalEntry',
                type: 'input',
                className: 'w-100',
                props: {
                  label: 'Redress number',
                  placeholder: 'Redress number',
                },
              },
            ],
          },
        ],
      },
      {
        fieldGroupClassName: 'd-flex flex-column g-5',
        wrappers: ['fold-card-wrapper'],
        props: {
          label: 'Preferences',
        },
        fieldGroup: [
          {
            fieldGroupClassName: 'd-flex flex-column flex-md-row g-5',
            fieldGroup: [
              {
                key: 'mealPreferencesId',
                type: 'dropdown',
                className: 'w-100',
                props: {
                  label: 'Meal preference',
                  placeholder: 'Select meal preference',
                  options: this.apiService.mealPreferencesOptions(),
                },
              },
              {
                key: 'seatPreferencesId',
                type: 'dropdown',
                className: 'w-100',
                props: {
                  label: 'Seat preference',
                  placeholder: 'Select seat preference',
                  options: this.apiService.seatPreferencesOptions(),
                },
              },
            ],
          },
          {
            fieldGroupClassName: 'd-flex flex-column flex-md-row g-5',
            fieldGroup: [
              {
                key: 'specialAssistanceId',
                type: 'dropdown',
                className: 'w-100',
                props: {
                  label: 'Special assistance',
                  placeholder: 'Select special assistance',
                  options: this.apiService.specialAssistanceOptions(),
                },
              },
              {
                key: 'petsId',
                type: 'dropdown',
                className: 'w-100',
                props: {
                  label: 'Traveling with pets',
                  placeholder: 'Select pet travel option',
                  options: this.apiService.petsOptions(),
                },
              },
            ],
          },
        ],
      },
    ];
  }

  back() {
    this.location.back();

    Tracking.track(PortalTrackingEvents.travelerPageCancelPressed);
  }

  override submit() {
    super.submit();

    Tracking.track(PortalTrackingEvents.travelerPageSubmitPressed);
  }

  override setErrors(errors: FormError[]) {
    const fieldErrors: { [key: string]: string } = {};

    errors.forEach((err) => {
      if (err.extensions?.field) {
        fieldErrors[err.extensions.field] = err.message;
      }
    });

    const errorsKeys = Object.keys(fieldErrors || {});

    if (errorsKeys?.length) {
      this.form.enable({ emitEvent: false });

      errorsKeys.forEach((errorKey) => {
        const field = this.form.get(errorKey);

        if (errorKey === 'phones' || errorKey === 'emails') {
          (field as FormGroup)?.controls[0]?.setErrors({ backend: fieldErrors[errorKey] });
        } else {
          field?.setErrors({ backend: fieldErrors[errorKey] });
        }
      });

      this.scrollToFirstError();
    }
  }
}
